// src/components/crm/UnflagModal.js
// PHASE 1.6 VERIFIER: r1-16-web-unflag-modal-16a3f2b8
//
// Modal for unflagging a note — removing the flag entirely. Distinct from
// resolving the flag (which keeps the flag and marks it done). Calls the
// Phase 1.6 unflagNote CF.
//
// Why two separate actions:
//   - Resolve: "I handled the situation, but keep the flag history visible."
//   - Unflag:  "This flag shouldn't have existed (created in error, or no
//              longer needed). Clear it entirely and delete pending bell
//              notifications."
//
// Permission tier (UX gate — defense-in-depth in the CF):
//   - Original note author
//   - High-level role (Owner / Admin / Manager / System Admin variants)
//   The flag target is NOT permitted to unflag — their grievance flow is
//   reassign, not unflag. NoteCard's canUnflag memo enforces this on the
//   client; the CF re-validates server-side.
//
// Mobile-friendly: bottom sheet on small screens, centered card on >=sm.
//
// ── Defense-in-depth notes ────────────────────────────────────────────────
//   1. The CF is the authoritative gate — this modal trusts that NoteCard's
//      permission check was correct, but the CF re-validates. A user who
//      somehow opened this modal without permission would be rejected at
//      the CF layer with HttpsError 'permission-denied'.
//   2. isSubmitting guard prevents double-submit on rapid clicks; the CF
//      transactional state machine is the second line of defense.
//   3. On success the modal closes and the realtime onSnapshot listener in
//      useOrgNotes will reflect the flag removal automatically — no explicit
//      refetch or local-state mutation needed.
//   4. CF errors surface their HttpsError .message to the user; falls back
//      to a generic message if .message is missing.

import React, { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { FlagOff, X } from 'lucide-react';

// Resolves once at module load against the default Firebase app — picks up
// any emulator connection set in firebase/config. If your config exports
// `functions` directly, you can swap in:
//   import { functions } from '../../firebase/config';
const unflagNoteFn = httpsCallable(getFunctions(), 'unflagNote');

const UnflagModal = ({ note, orgId, onClose }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (isSubmitting) return;
    if (!orgId || !note?.id) {
      setError('Missing required information.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await unflagNoteFn({
        orgId,
        noteId: note.id,
      });
      onClose();
    } catch (err) {
      console.error('UnflagModal: unflag failed', err);
      setError(err?.message || 'Could not unflag note. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-slate-900/40 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-t-xl sm:rounded-xl w-full sm:max-w-md shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-slate-100">
          <h3 className="text-base font-semibold text-slate-800">Unflag note</h3>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="text-slate-400 hover:text-slate-600 p-1 rounded-md disabled:opacity-50"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-4 space-y-3">
          <p className="text-sm text-slate-600">
            Remove the flag from this note entirely? The flag will be cleared
            and any pending notifications will be deleted.
          </p>
          <p className="text-sm text-slate-600">
            <span className="font-semibold text-slate-800">Different from Resolve:</span>{' '}
            use <span className="font-semibold">Resolve</span> if you've handled
            the situation but want the flag and its history preserved. Use{' '}
            <span className="font-semibold">Unflag</span> only if the flag was
            created in error or is no longer needed.
          </p>
          <p className="text-xs text-slate-500">
            The note itself stays in place; only the flag is removed. The audit
            trail (who flagged it and when) is preserved on the note.
          </p>

          {error && (
            <p className="text-xs text-red-600">{error}</p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-4 pt-0">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="text-sm font-semibold px-4 py-2 rounded-lg text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FlagOff size={14} />
            {isSubmitting ? 'Unflagging…' : 'Unflag'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UnflagModal;